import { useMemo } from 'react';
import { UserRole } from '../types/auth.types';
import { useAuth } from './useAuth';

/**
 * Simplified permission definitions for each role
 * Uses basic CRUD operations: View, Create, Edit, Delete
 *
 * SUPER_ADMIN: Full access — family founder (highest rank)
 * PARENT: Full access - can view, create, edit, and delete
 * CHILD: Read-only access - can only view content
 */
const ROLE_PERMISSIONS = {
  [UserRole.SUPER_ADMIN]: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
  },
  [UserRole.PARENT]: {
    canView: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
  },
  [UserRole.CHILD]: {
    canView: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
  },
} as const;

/**
 * Numeric rank of each role. Higher rank = more authority.
 *
 * A DELIBERATE COPY, and the last one. The ladder is declared in three places
 * that cannot import from each other: `public.role_rank()`
 * (supabase/migrations/010_family_first_signup.sql) is the source of truth and
 * the only one that decides anything, `supabase/functions/_shared/roleRank.ts`
 * is the canonical TypeScript copy for the Edge Functions, and this is the
 * browser's.
 *
 * WHY IT IS NOT COLLAPSED ONTO THE SHARED MODULE — narrowed, because the old
 * reason here was wrong. This comment used to say that `src/` and
 * `supabase/functions/` are different runtimes with different tsconfigs "so an
 * import across that boundary would not build". That is false, and measurably
 * so: `src/features/auth/__tests__/adminPasswordRank.test.ts` already imports
 * `supabase/functions/_shared/roleRank.ts` directly, and Wave 8 added a second
 * such import (`src/features/family-calendar/utils/recurrence.utils.ts` ->
 * `_shared/rrule.ts`) that vite bundles and tsc typechecks with no config
 * change. tsc follows imports wherever they lead, regardless of what
 * tsconfig's `include` globs list as roots.
 *
 * The two ACTUAL blockers are narrower:
 *   1. Import specifiers. Only a ZERO-IMPORT LEAF crosses cleanly. An Edge
 *      module that imports a sibling with a `.ts` specifier, or over `jsr:` /
 *      `https:`, does not resolve under vite.
 *   2. The strictness delta. Once `src/` imports an Edge module, the APP's
 *      flags (noUncheckedIndexedAccess, exactOptionalPropertyTypes,
 *      noImplicitReturns) police it, and Deno's config does not set them — so
 *      an edit that is fine for Deno can break the client build. `dayCode()` in
 *      `_shared/rrule.ts` carries a `?? 'SU'` that exists purely for that
 *      reason, and its comment says so.
 *
 * This particular table stays duplicated because a UserRole enum is a `src/`
 * concept the Edge copy deliberately does not share (it keys on a plain
 * `RoleName` string union), not because the boundary is uncrossable.
 *
 * What replaces the import is a test:
 * src/shared/__tests__/roleRankParity.test.ts imports both tables and fails if
 * they disagree. Change one and change the other — and remember that the
 * numbers here decide nothing on their own. Every rank that matters is
 * re-decided server-side, by RLS and by the Edge Functions; this table only
 * chooses which controls a page renders.
 */
const ROLE_RANK: Readonly<Record<UserRole, number>> = {
  [UserRole.SUPER_ADMIN]: 3,
  [UserRole.PARENT]: 2,
  [UserRole.CHILD]: 1,
} as const;

/**
 * Numeric rank of a role (SUPER_ADMIN=3 > PARENT=2 > CHILD=1).
 */
export function roleRank(role: UserRole): number {
  return ROLE_RANK[role];
}

/**
 * Whether a member with `own` role may assign the `target` role (invitations
 * and role changes). Mirrors the server-side rank cap — nobody can assign a
 * role higher than their own. The server remains the source of truth.
 */
export function canAssignRole(own: UserRole, target: UserRole): boolean {
  return roleRank(own) >= roleRank(target);
}

/**
 * Available permissions in the system
 */
export type Permission = 'canView' | 'canCreate' | 'canEdit' | 'canDelete';

interface UsePermissionsReturn {
  readonly permissions: (typeof ROLE_PERMISSIONS)[UserRole] | null;
  readonly hasPermission: (permission: Permission) => boolean;
  readonly hasAllPermissions: (permissions: readonly Permission[]) => boolean;
  readonly hasAnyPermission: (permissions: readonly Permission[]) => boolean;
  readonly canAccess: (requiredRoles: readonly UserRole[]) => boolean;
}

/**
 * Custom hook for checking user permissions and role-based access
 *
 * @example
 * const { hasPermission, canAccess } = usePermissions();
 *
 * if (hasPermission('canCreate')) {
 *   // Show create button
 * }
 *
 * if (hasPermission('canEdit')) {
 *   // Show edit controls
 * }
 *
 * if (canAccess([UserRole.PARENT])) {
 *   // Render parent-only content
 * }
 */
export function usePermissions(): UsePermissionsReturn {
  const { role, isAuthenticated } = useAuth();

  const permissions = useMemo(() => {
    if (!isAuthenticated || !role) {
      return null;
    }
    return ROLE_PERMISSIONS[role];
  }, [isAuthenticated, role]);

  const hasPermission = useMemo(
    () => (permission: Permission) => {
      if (!permissions) return false;
      return permissions[permission] ?? false;
    },
    [permissions]
  );

  const hasAllPermissions = useMemo(
    () => (requiredPermissions: readonly Permission[]) => {
      if (!permissions) return false;
      return requiredPermissions.every(
        (permission) => permissions[permission] ?? false
      );
    },
    [permissions]
  );

  const hasAnyPermission = useMemo(
    () => (requiredPermissions: readonly Permission[]) => {
      if (!permissions) return false;
      return requiredPermissions.some(
        (permission) => permissions[permission] ?? false
      );
    },
    [permissions]
  );

  const canAccess = useMemo(
    () => (requiredRoles: readonly UserRole[]) => {
      if (!isAuthenticated || !role) return false;
      return requiredRoles.includes(role);
    },
    [isAuthenticated, role]
  );

  return {
    permissions,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    canAccess,
  };
}
